import { Link } from "react-router-dom";
import SEOMeta from "@/components/base/SEOMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import HealthDisclaimer from "@/components/base/HealthDisclaimer";

const whatYoullLearn = [
  {
    title: "Understanding Your Patterns",
    description: "Learn to recognise the emotional and situational triggers that shape your drinking habits, without shame or self-criticism.",
    icon: "ri-search-eye-line",
  },
  {
    title: "Building New Coping Tools",
    description: "Discover gentle, practical strategies to manage stress, anxiety, and difficult emotions without reaching for alcohol.",
    icon: "ri-tools-line",
  },
  {
    title: "Creating Your Evening Ritual",
    description: "Design a personalised wind-down routine that genuinely nourishes you and replaces the evening drink with something more fulfilling.",
    icon: "ri-moon-line",
  },
  {
    title: "Setting Boundaries with Grace",
    description: "Learn how to navigate social situations, family pressures, and work events while staying true to your goals.",
    icon: "ri-shield-line",
  },
  {
    title: "Rebuilding Self-Trust",
    description: "Reconnect with your inner wisdom and rebuild the confidence that comes from keeping promises to yourself.",
    icon: "ri-heart-line",
  },
  {
    title: "Sustaining Your Progress",
    description: "Develop a long-term framework for maintaining your new relationship with alcohol, including how to handle setbacks kindly.",
    icon: "ri-seedling-line",
  },
];

const courseStructure = [
  {
    title: "Weekly Live Sessions",
    description: "Intimate group coaching calls where you can share as much or as little as feels comfortable. These sessions are recorded so you never miss a thing.",
    icon: "ri-vidicon-line",
  },
  {
    title: "Guided Workbooks",
    description: "Beautifully designed reflection exercises and journaling prompts to deepen your learning between sessions.",
    icon: "ri-book-open-line",
  },
  {
    title: "Private Community",
    description: "A quiet, moderated space to connect with others on a similar path — completely free from the noise of social media.",
    icon: "ri-user-heart-line",
  },
  {
    title: "One-to-One Check-ins",
    description: "Regular private messages from Serena to support you personally, answer questions, and help you stay on track.",
    icon: "ri-chat-smile-2-line",
  },
];

export default function Course() {
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "The Quiet Recovery Space Programme",
    "description": "A gentle, private online programme for people who want to change their relationship with alcohol without labels, pressure, or judgement. Includes live coaching sessions, guided workbooks, private community, and one-to-one support from Serena Raimes.",
    "provider": {
      "@type": "Organization",
      "name": "Serena's Quiet Recovery Space",
      "url": "https://serenasquietrecoveryspace.com",
    },
    "instructor": {
      "@type": "Person",
      "name": "Serena Raimes",
      "jobTitle": "Recovery Coach",
      "url": "https://serenasquietrecoveryspace.com/about",
    },
    "url": "https://serenasquietrecoveryspace.com/course",
    "offers": {
      "@type": "Offer",
      "url": "https://sraimes43.systeme.io/course",
    },
  };

  return (
    <main className="min-h-screen pt-20 md:pt-24">
      <SEOMeta
        title="Quiet Recovery Programme | Private Alcohol Support Course | Serena's Quiet Recovery Space"
        description="A gentle, private online programme for people who want to change their relationship with alcohol — without labels, pressure, or judgement. Live coaching, workbooks, community, and one-to-one support."
        canonicalUrl="/course"
        ogType="website"
        schema={courseSchema}
      />

      {/* Hero */}
      <section className="relative w-full py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20soft%20sunlit%20window%20seat%20with%20a%20warm%20knitted%20blanket%20draped%20over%20it%2C%20a%20cup%20of%20herbal%20tea%20on%20a%20wooden%20side%20table%2C%20an%20open%20journal%20with%20a%20fountain%20pen%2C%20soft%20morning%20light%20streaming%20through%20sheer%20curtains%2C%20calm%20and%20gentle%20atmosphere%2C%20warm%20beige%20and%20cream%20tones%2C%20peaceful%20sanctuary%20mood%2C%20editorial%20interior%20photography%20with%20soft%20focus%20and%20warm%20highlights&width=1600&height=900&seq=course-hero-2026-v1&orientation=landscape"
            alt="A gentle peaceful window seat with warm knitted blanket, herbal tea, and open journal, soft morning light, calm sanctuary atmosphere"
            title="The Quiet Recovery Space Programme - Private Alcohol Recovery Coaching | Serena's Quiet Recovery Space"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/35"></div>
        </div>
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <Breadcrumbs
              items={[
                { label: "Home", href: "/" },
                { label: "The Programme" },
              ]}
            />
            <p className="font-body text-xs md:text-sm text-background-300 tracking-widest uppercase mb-4">
              A Gentle Online Programme
            </p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-background-50 mb-6 leading-tight">
              The Quiet Recovery Space Programme
            </h1>
            <p className="font-body text-sm md:text-base text-background-200 leading-relaxed max-w-2xl mx-auto mb-8">
              A private, supportive online course for people who want to change their relationship with alcohol — without labels, pressure, or judgement. Step into a space where you can be honest about where you are and gently move toward where you want to be.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://sraimes43.systeme.io/course"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-block bg-primary-500 text-background-50 font-body text-sm font-medium px-8 py-3 rounded-md hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Enrol Now
              </a>
              <a
                href="#what-to-expect"
                className="inline-block font-body text-sm text-background-100 hover:text-background-50 transition-colors duration-300 underline underline-offset-4"
              >
                See what to expect
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground-950 mb-4 text-center">
              This programme is for you if...
            </h2>
            <p className="font-body text-sm text-foreground-600 text-center mb-10 max-w-2xl mx-auto leading-relaxed">
              You do not need to have everything figured out. You only need a quiet place to begin.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "You are drinking more than you would like and want to change that — quietly, privately, at your own pace",
                "You feel ashamed or embarrassed about your drinking and have never told anyone how much it worries you",
                "You have tried to cut back before but struggled to make it stick on your own",
                "The idea of a traditional recovery programme feels too intense, too public, or simply not right for you",
                "You want a gentler, more compassionate approach that respects your autonomy and your privacy",
                "You are ready to invest time in yourself and believe that meaningful, lasting change is possible",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-background-100 rounded-lg p-4 md:p-5">
                  <div className="w-6 h-6 flex-shrink-0 mt-0.5 flex items-center justify-center">
                    <i className="ri-check-line text-base text-accent-600" />
                  </div>
                  <p className="font-body text-sm text-foreground-700 leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="w-full bg-background-100 py-16 md:py-24" id="what-to-expect">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground-950 mb-4 text-center">
              What you will explore together
            </h2>
            <p className="font-body text-sm text-foreground-600 text-center mb-12 max-w-2xl mx-auto leading-relaxed">
              Each module is designed to be gentle, practical, and genuinely useful — no overwhelm, no rigid rules, just quiet support.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {whatYoullLearn.map((item, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/60">
                  <div className="w-10 h-10 mb-4 flex items-center justify-center rounded-md bg-accent-100">
                    <i className={`${item.icon} text-lg text-accent-700`} />
                  </div>
                  <h3 className="font-heading text-base font-medium text-foreground-950 mb-2">
                    {item.title}
                  </h3>
                  <p className="font-body text-sm text-foreground-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Course Structure */}
      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground-950 mb-4 text-center">
              How the programme works
            </h2>
            <p className="font-body text-sm text-foreground-600 text-center mb-12 max-w-2xl mx-auto leading-relaxed">
              Everything is designed to fit quietly into your life — no pressure to show up in a certain way or at a certain pace.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
              {courseStructure.map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-background-100 rounded-lg p-5 md:p-6">
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className={`${item.icon} text-xl text-secondary-700`} />
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-medium text-foreground-950 mb-1">
                      {item.title}
                    </h3>
                    <p className="font-body text-sm text-foreground-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Makes This Different */}
      <section className="w-full bg-background-100 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground-950 mb-6">
              Why this programme is different
            </h2>
            <div className="space-y-4 mb-10">
              <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed">
                Most recovery programmes ask you to label yourself, follow rigid rules, and attend public meetings. That approach works for some people — but not for everyone.
              </p>
              <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed">
                <strong>The Quiet Recovery Space Programme</strong> is built on a different philosophy. There are no labels here. No shame. No requirement to call yourself anything you are not comfortable with. You set your own goals — whether that is moderation, a break, or sobriety — and we walk beside you at your pace.
              </p>
              <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed">
                This is not a clinical programme. It is a <strong>human one</strong> — grounded in real experience, genuine compassion, and the quiet belief that you already have everything you need to heal. You just need a safe space to discover it.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-left">
              <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/60 max-w-xs w-full">
                <div className="w-10 h-10 mb-3 flex items-center justify-center rounded-md bg-accent-100">
                  <i className="ri-close-line text-lg text-accent-700" />
                </div>
                <p className="font-heading text-xs font-medium text-foreground-500 uppercase tracking-wide mb-2">Not This</p>
                <ul className="space-y-2">
                  {["Labels and stigma", "Rigid, one-size-fits-all rules", "Public meetings", "Shame-based motivation", "Pressure to identify a certain way"].map((item, i) => (
                    <li key={i} className="font-body text-sm text-foreground-600 flex items-start gap-2">
                      <span className="text-accent-600 mt-1 text-xs">&#10005;</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-accent-200 max-w-xs w-full">
                <div className="w-10 h-10 mb-3 flex items-center justify-center rounded-md bg-accent-500">
                  <i className="ri-check-line text-lg text-background-50" />
                </div>
                <p className="font-heading text-xs font-medium text-foreground-500 uppercase tracking-wide mb-2">This Instead</p>
                <ul className="space-y-2">
                  {["Compassionate, private support", "Flexible goals you set yourself", "A quiet, intimate space", "Encouragement and gentle guidance", "Privacy, respect, and autonomy"].map((item, i) => (
                    <li key={i} className="font-body text-sm text-foreground-600 flex items-start gap-2">
                      <span className="text-accent-600 mt-1 text-xs">&#10003;</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Serena */}
      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
              <div className="w-full md:w-2/5">
                <div className="relative overflow-hidden rounded-lg">
                  <img
                    src="https://sraimes43-pzoau.wordpress.com/wp-content/uploads/2026/06/chatgpt-image-mar-24-2026-02_49_51-pm.png"
                    alt="Serena Raimes - Addiction Recovery Coach and Programme Guide"
                    title="Serena Raimes - Your Guide Through The Quiet Recovery Space Programme"
                    width="1024"
                    height="1024"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
              <div className="w-full md:w-3/5">
                <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground-950 mb-4">
                  Your guide through this journey
                </h2>
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                  Serena Raimes is an <strong>addiction recovery coach</strong> who has walked this path herself. Sixteen years ago, she lost her husband and navigated life as a single parent through grief, depression, and the search for a way forward.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                  Today, she is a successful entrepreneur dedicated to helping others overcome addiction and create lives they can be proud of. Her approach is grounded in <strong>real lived experience</strong>, not just theory.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                  In this programme, Serena brings her warmth, wisdom, and unwavering belief in every person&apos;s capacity for change — and she will be with you every step of the way.
                </p>
                <Link
                  to="/about"
                  className="inline-block font-body text-sm text-accent-700 hover:text-accent-800 transition-colors duration-300 underline underline-offset-4"
                >
                  Read Serena&apos;s full story
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full bg-background-100 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-light text-foreground-950 mb-10 text-center">
              Questions you might be asking
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "I am not sure if I am 'ready.' Can I still join?",
                  a: "Absolutely. You do not need to feel ready or certain. The programme is designed to meet you exactly where you are — even if that place feels uncertain or hesitant. Sometimes the quietest step forward is the one that matters most.",
                },
                {
                  q: "Is this programme suitable if I do not want to stop drinking completely?",
                  a: "Yes. You set your own goals. Whether you want to cut back, take a break, explore moderation, or work toward sobriety, the programme supports your personal direction without judgement.",
                },
                {
                  q: "How much time will I need each week?",
                  a: "The live sessions run about 60 to 90 minutes each week, and the workbook exercises are designed to fit around your life — usually 15 to 30 minutes a few times a week. You can engage as deeply or as lightly as your schedule allows.",
                },
                {
                  q: "Will anyone else know I am in this programme?",
                  a: "Your privacy is paramount. The community space is private and not visible to the public. You can use your first name only or a pseudonym. Nothing is shared outside the programme, ever.",
                },
                {
                  q: "What if I fall behind or miss a session?",
                  a: "All live sessions are recorded and available to watch whenever suits you. There is no falling behind — the programme moves at your pace, and Serena checks in with you personally to help you stay connected.",
                },
                {
                  q: "How is this different from therapy or a clinical programme?",
                  a: "This is a coaching and peer-support programme, not a clinical treatment. It does not replace medical or therapeutic care. It complements your existing support by providing a gentle, private space for guided self-discovery, practical tools, and compassionate community.",
                },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-lg border border-background-200/60">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-body text-sm md:text-base font-medium text-foreground-900 list-none">
                    {faq.q}
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-3 group-open:hidden">
                      <i className="ri-add-line text-foreground-500" />
                    </span>
                    <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-3 hidden group-open:flex">
                      <i className="ri-subtract-line text-foreground-500" />
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="font-body text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative w-full py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20serene%20peaceful%20morning%20scene%20with%20a%20wooden%20desk%20by%20a%20window%2C%20a%20handwritten%20journal%20open%20with%20a%20pen%20resting%20on%20it%2C%20a%20steaming%20cup%20of%20tea%2C%20soft%20natural%20daylight%20illuminating%20the%20space%2C%20a%20cosy%20armchair%20with%20a%20cream%20throw%20blanket%20in%20the%20background%2C%20warm%20calming%20beige%20and%20soft%20green%20tones%2C%20editorial%20lifestyle%20photography%2C%20quiet%20hopeful%20restorative%20atmosphere&width=1600&height=800&seq=course-cta-2026-v1&orientation=landscape"
            alt="A serene peaceful morning desk scene with open journal, steaming tea, soft daylight, and cosy armchair, quiet hopeful restorative atmosphere"
            title="Enrol in The Quiet Recovery Space Programme - Serena's Quiet Recovery Space"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/35"></div>
        </div>
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-background-50 mb-6">
              Your quiet space is waiting
            </h2>
            <p className="font-body text-sm md:text-base text-background-200 leading-relaxed mb-3">
              You do not need to have everything figured out. You do not need to be certain. You only need to be willing to take one small, gentle step toward the life you want.
            </p>
            <p className="font-body text-sm md:text-base text-background-300 leading-relaxed mb-10">
              Join <strong>The Quiet Recovery Space Programme</strong> and discover what becomes possible when you give yourself the space, support, and compassion you deserve.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://sraimes43.systeme.io/course"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-block bg-accent-500 text-background-50 font-body text-sm font-medium px-8 py-3 rounded-md hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Enrol Now
              </a>
              <Link
                to="/contact"
                className="inline-block font-body text-sm text-background-100 hover:text-background-50 transition-colors duration-300 underline underline-offset-4"
              >
                Have a question? Get in touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <HealthDisclaimer variant="box" />
          </div>
        </div>
      </section>
    </main>
  );
}